import type { Room } from '@hollowveil/shared';

export const roomWonderland: Room = {
  id: 'room_wonderland',
  name: "Pays des Merveilles - Jardin Victorien",
  description: "Un paisible jardin victorien anglais. Mais derrière les roses se cache un monde de folie...",
  size: [24, 10, 24],
  floorStyle: 'grass',
  ambiance: {
    realWorld: {
      backgroundColor: '#87CEEB',
      ambientLightColor: '#FFF8DC',
      ambientLightIntensity: 0.9,
    },
    spectralWorld: {
      // Wonderland tordu - couleurs vives mais inquiétantes
      backgroundColor: '#2a0a3a',
      ambientLightColor: '#ff69b4',
      ambientLightIntensity: 0.4,
      fogColor: '#4a1a6a',
      fogDensity: 0.05,
    },
  },
  objects: [
    // === MONDE RÉEL - JARDIN VICTORIEN ===

    // Haies et topiaires
    {
      id: 'obj_hedge_1',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [2, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_hedge_2',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [6, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_hedge_3',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [10, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_topiary_1',
      name: 'Topiaire',
      type: 'static',
      modelId: 'topiary',
      position: [4, 0, 6],
      rotation: [0, 0.5, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Gazebo victorien
    {
      id: 'obj_gazebo',
      name: 'Gazebo',
      type: 'interactive',
      modelId: 'gazebo',
      position: [12, 0, 12],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Un élégant gazebo victorien. L'endroit parfait pour prendre le thé.",
          },
        },
      ],
    },

    // Table de thé (monde réel - normale)
    {
      id: 'obj_tea_table_real',
      name: 'Table de Jardin',
      type: 'interactive',
      modelId: 'tea_table',
      position: [8, 0, 10],
      rotation: [0, 0.3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Une table de jardin avec un service à thé en porcelaine fine.",
          },
        },
      ],
    },

    // Fontaine
    {
      id: 'obj_fountain',
      name: 'Fontaine',
      type: 'interactive',
      modelId: 'fountain',
      position: [18, 0, 8],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Une fontaine classique. L'eau murmure paisiblement.",
          },
        },
      ],
    },

    // Roses blanches
    {
      id: 'obj_roses_white_1',
      name: 'Rosier Blanc',
      type: 'static',
      modelId: 'rose_bush',
      position: [6, 0, 14],
      rotation: [0, 0, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { color: 'white' },
    },
    {
      id: 'obj_roses_white_2',
      name: 'Rosier Blanc',
      type: 'static',
      modelId: 'rose_bush',
      position: [10, 0, 16],
      rotation: [0, 0.4, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { color: 'white' },
    },

    // Banc de jardin
    {
      id: 'obj_garden_bench',
      name: 'Banc de Jardin',
      type: 'interactive',
      modelId: 'bench',
      position: [20, 0, 12],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Un banc en fer forgé. On pourrait y lire un bon livre.",
          },
        },
      ],
    },

    // Cadran solaire
    {
      id: 'obj_sundial',
      name: 'Cadran Solaire',
      type: 'interactive',
      modelId: 'sundial',
      position: [16, 0, 18],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Un cadran solaire ancien. Le temps passe si lentement ici...",
          },
        },
      ],
    },

    // === MONDE SPECTRAL - WONDERLAND TORDU ===

    // Terrier du Lapin Blanc
    {
      id: 'obj_rabbit_hole',
      name: 'Terrier du Lapin',
      type: 'interactive',
      modelId: 'rabbit_hole',
      position: [4, 0, 4],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Le terrier du Lapin Blanc. Tomber dedans, c'est tomber dans la folie...",
          },
        },
      ],
    },

    // Table du Chapelier Fou (version tordue)
    {
      id: 'obj_mad_tea_party',
      name: 'Table du Thé Fou',
      type: 'interactive',
      modelId: 'mad_tea_table',
      position: [8, 0, 10],
      rotation: [0, 0.3, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Une place ! Une place ! Pas de place ! Pourquoi un corbeau ressemble-t-il à un bureau ?",
          },
        },
      ],
    },

    // Champignons géants
    {
      id: 'obj_mushroom_1',
      name: 'Champignon Géant',
      type: 'interactive',
      modelId: 'giant_mushroom',
      position: [14, 0, 6],
      rotation: [0, 0.5, 0],
      scale: [2, 2.5, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { color: 'red' },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Un côté vous fera grandir, l'autre vous fera rapetisser...",
          },
        },
      ],
    },
    {
      id: 'obj_mushroom_2',
      name: 'Champignon Géant',
      type: 'static',
      modelId: 'giant_mushroom',
      position: [18, 0, 4],
      rotation: [0, -0.3, 0],
      scale: [1.8, 2.2, 1.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { color: 'blue' },
    },
    {
      id: 'obj_mushroom_3',
      name: 'Champignon Géant',
      type: 'static',
      modelId: 'giant_mushroom',
      position: [22, 0, 10],
      rotation: [0, 1.2, 0],
      scale: [1.5, 1.8, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { color: 'purple' },
    },

    // Château de la Reine de Cœur
    {
      id: 'obj_queen_castle',
      name: 'Château de la Reine',
      type: 'interactive',
      modelId: 'queen_castle',
      position: [20, 0, 20],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Le Château de la Reine de Cœur. QU'ON LUI COUPE LA TÊTE !",
          },
        },
      ],
    },

    // Roses peintes en rouge (ensanglantées)
    {
      id: 'obj_roses_red_1',
      name: 'Roses Rouges',
      type: 'interactive',
      modelId: 'rose_bush',
      position: [6, 0, 14],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { color: 'blood_red' },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Des roses blanches peintes en rouge... ou est-ce du sang ?",
          },
        },
      ],
    },
    {
      id: 'obj_roses_red_2',
      name: 'Roses Rouges',
      type: 'static',
      modelId: 'rose_bush',
      position: [10, 0, 16],
      rotation: [0, 0.4, 0],
      scale: [1.4, 1.4, 1.4],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { color: 'blood_red' },
    },

    // Cartes soldats
    {
      id: 'obj_card_soldier_1',
      name: 'Carte Soldat',
      type: 'static',
      modelId: 'card_soldier',
      position: [16, 0, 16],
      rotation: [0, Math.PI / 6, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { suit: 'hearts' },
    },
    {
      id: 'obj_card_soldier_2',
      name: 'Carte Soldat',
      type: 'static',
      modelId: 'card_soldier',
      position: [18, 0, 18],
      rotation: [0, -Math.PI / 6, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { suit: 'spades' },
    },

    // Horloge détraquée
    {
      id: 'obj_broken_clock',
      name: 'Horloge Folle',
      type: 'interactive',
      modelId: 'broken_clock',
      position: [12, 0, 20],
      rotation: [0, 0, 0.2],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Il est toujours 6 heures ici. L'heure du thé... pour toujours.",
          },
        },
      ],
    },

    // Tasses volantes
    {
      id: 'obj_flying_teacup_1',
      name: 'Tasse Volante',
      type: 'static',
      modelId: 'flying_teacup',
      position: [10, 2, 8],
      rotation: [0.2, 0.5, 0.1],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_flying_teacup_2',
      name: 'Tasse Volante',
      type: 'static',
      modelId: 'flying_teacup',
      position: [6, 1.5, 12],
      rotation: [-0.1, 1.2, 0.2],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },

    // Porte vers nulle part
    {
      id: 'obj_nowhere_door',
      name: 'Porte de Nulle Part',
      type: 'interactive',
      modelId: 'strange_door',
      position: [2, 0, 18],
      rotation: [0, Math.PI / 3, 0],
      scale: [1.5, 2, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: "Une porte qui ne mène nulle part... ou partout à la fois ?",
          },
        },
      ],
    },

    // === OBJETS COLLECTIBLES ===
    {
      id: 'obj_pocket_watch',
      name: 'Montre à Gousset',
      type: 'interactive',
      modelId: 'pocket_watch',
      position: [5, 0.3, 5],
      rotation: [0.5, 0.3, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'white_rabbit_watch',
            itemName: 'Montre du Lapin Blanc',
            itemDescription: "En retard ! En retard ! Je suis en retard !",
            itemIcon: 'star',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: "Vous trouvez une montre à gousset dorée. Elle semble toujours indiquer 6 heures...",
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_pocket_watch' },
        },
      ],
    },

    // Fiole "Bois-moi"
    {
      id: 'obj_drink_me',
      name: 'Fiole "Bois-moi"',
      type: 'interactive',
      modelId: 'potion_bottle',
      position: [14, 0.3, 12],
      rotation: [0, 0, 0],
      scale: [0.6, 0.6, 0.6],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'drink_me_potion',
            itemName: 'Potion "Bois-moi"',
            itemDescription: "Une potion qui change votre taille. Attention aux effets secondaires !",
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: "Une petite fiole avec une étiquette 'BOIS-MOI'. Oseriez-vous ?",
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_drink_me' },
        },
      ],
    },
  ],
  puzzles: [],
  connections: [],
  initialWorld: 'real',
  hasSpectralLayer: true,
  palette: 'forest',
  biome: 'forest',
  weather: 'none',
  weatherIntensity: 0.2,
  npcs: [
    // === MONDE RÉEL - PERSONNAGES NORMAUX ===
    {
      id: 'npc_victorian_lady',
      type: 'victorian_lady',
      name: 'Dame Victorienne',
      position: [10, 0, 12],
      scale: 3,
      idleAnimation: 'stand',
      petAnimation: 'curtsy',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'real',
    },
    {
      id: 'npc_gardener',
      type: 'gardener',
      name: 'Jardinier',
      position: [6, 0, 16],
      scale: 3,
      idleAnimation: 'work',
      petAnimation: 'tip_hat',
      petRadius: 2,
      petCooldown: 2500,
      isPettable: true,
      visibleInWorld: 'real',
    },
    {
      id: 'npc_alice_real',
      type: 'alice_normal',
      name: 'Alice',
      position: [8, 0, 8],
      scale: 2.5,
      idleAnimation: 'curious',
      petAnimation: 'happy',
      petRadius: 2,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'real',
    },

    // === MONDE SPECTRAL - PERSONNAGES DE WONDERLAND ===
    {
      id: 'npc_white_rabbit',
      type: 'white_rabbit',
      name: 'Lapin Blanc',
      position: [4, 0, 6],
      scale: 2.5,
      idleAnimation: 'nervous',
      petAnimation: 'panic',
      petRadius: 2,
      petCooldown: 1500,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_mad_hatter',
      type: 'mad_hatter',
      name: 'Chapelier Fou',
      position: [8, 0, 10],
      scale: 3.5,
      idleAnimation: 'crazy',
      petAnimation: 'laugh',
      petRadius: 3,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_march_hare',
      type: 'march_hare',
      name: 'Lièvre de Mars',
      position: [10, 0, 9],
      scale: 3,
      idleAnimation: 'twitch',
      petAnimation: 'excited',
      petRadius: 2.5,
      petCooldown: 1800,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_cheshire_cat',
      type: 'cheshire_cat',
      name: 'Chat du Cheshire',
      position: [16, 2, 8],
      scale: 3,
      idleAnimation: 'float',
      petAnimation: 'grin',
      petRadius: 3,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_caterpillar',
      type: 'caterpillar',
      name: 'Chenille',
      position: [14, 1.5, 6],
      scale: 3,
      idleAnimation: 'smoke',
      petAnimation: 'puff',
      petRadius: 2.5,
      petCooldown: 4000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_queen_of_hearts',
      type: 'queen_of_hearts',
      name: 'Reine de Cœur',
      position: [18, 0, 18],
      scale: 4,
      idleAnimation: 'imperious',
      petAnimation: 'rage',
      petRadius: 4,
      petCooldown: 8000,
      isPettable: false,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_tweedledee',
      type: 'tweedle',
      name: 'Tweedledee',
      position: [12, 0, 16],
      scale: 2.8,
      idleAnimation: 'bounce',
      petAnimation: 'argue',
      petRadius: 2,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    {
      id: 'npc_tweedledum',
      type: 'tweedle',
      name: 'Tweedledum',
      position: [14, 0, 16],
      scale: 2.8,
      idleAnimation: 'bounce',
      petAnimation: 'argue',
      petRadius: 2,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
  ],
};
